import type { ChangeEvent } from "react";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import { INVENTORY_STATUSES } from "@/api/inventory/_models/Inventory";
import type { InventoryStatus } from "@/api/inventory/_models/Inventory";
import { ClearFiltersButton } from "@/components/ClearFiltersButton";
import { MultiSelectFilter } from "@/components/MultiSelectFilter";
import { SidePanel } from "@/components/SidePanel";
import { InventoryListStatusChip } from "@/features/inventory/inventory-list/InventoryListStatusChip";
import type { InventoryLocationCount } from "@/api/inventory/_models/Inventory";

/** The inventory list's filter values, sent to GET /inventory-levels (?search=&status=&locationId=). */
export interface InventoryListFilterValues {
  /** Text in the SKU or product name. */
  search: string;
  /** Empty means every status. */
  statuses: InventoryStatus[];
  /** Our location ids; empty means every location. */
  locationIds: string[];
}

/** No filter: every row. */
export const INVENTORY_LIST_EMPTY_FILTER: InventoryListFilterValues = {
  search: "",
  statuses: [],
  locationIds: [],
};

/** Whether any filter narrows the list. */
export const isInventoryListFilterActive = (filter: InventoryListFilterValues): boolean => {
  return filter.search.trim() !== "" || filter.statuses.length > 0 || filter.locationIds.length > 0;
};

interface InventoryListFilterPanelProps {
  filter: InventoryListFilterValues;
  isOpen: boolean;
  locations: InventoryLocationCount[];
  statusCounts: Partial<Record<InventoryStatus, number>>;
  onChange: (filter: InventoryListFilterValues) => void;
  onClose: () => void;
}

/** The inventory list's filters in a side panel: search, statuses (with whole-inventory counts) and locations. Changes apply as they're made. */
export const InventoryListFilterPanel = (props: InventoryListFilterPanelProps) => {
  const { filter, isOpen, locations, statusCounts, onChange, onClose } = props;
  const locationIds = locations.map((location) => location.id);
  const locationNameById = new Map(locations.map((location) => [location.id, location.name]));

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...filter, search: event.target.value });
  };

  const handleStatusesChange = (statuses: InventoryStatus[]) => {
    onChange({ ...filter, statuses });
  };

  const handleLocationIdsChange = (nextLocationIds: string[]) => {
    onChange({ ...filter, locationIds: nextLocationIds });
  };

  const handleClear = () => {
    onChange(INVENTORY_LIST_EMPTY_FILTER);
  };

  const renderStatus = (status: InventoryStatus) => {
    return (
      <InventoryListStatusChip
        count={statusCounts[status] ?? 0}
        status={status}
      />
    );
  };

  const toLocationName = (locationId: string): string => {
    return locationNameById.get(locationId) ?? locationId;
  };

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title="Filter inventory"
    >
      <Stack
        spacing={2}
        sx={{
          flex: 1,
          mt: 0.5,
        }}
        useFlexGap
      >
        <TextField
          autoFocus
          fullWidth
          onChange={handleSearchChange}
          placeholder="SKU or product…"
          size="small"
          value={filter.search}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <MultiSelectFilter
          allLabel="All statuses"
          onChange={handleStatusesChange}
          options={INVENTORY_STATUSES}
          renderOption={renderStatus}
          value={filter.statuses}
        />
        <MultiSelectFilter
          allLabel="All locations"
          getOptionLabel={toLocationName}
          onChange={handleLocationIdsChange}
          options={locationIds}
          value={filter.locationIds}
        />
        <ClearFiltersButton
          isActive={isInventoryListFilterActive(filter)}
          onClick={handleClear}
        />
      </Stack>
    </SidePanel>
  );
};
